/** @jsxImportSource react */
import { useState } from 'react'
import { Box, Checkbox, IconButton, Link, Menu, MenuItem, Tooltip, Typography } from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import { Link as RouterLink } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { tasksAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'
import { getErrorMessage } from '../../utils/apiErrors'
import { formatDate } from '../../utils/helpers'
import { useNotify } from '../common/NotificationProvider'
import ConfirmDialog from '../common/ConfirmDialog'
import { REMINDERS, dueLabel } from './taskModel'

/** One task row: tick to complete, due time (red when overdue), assignee, record link, menu. */
const TaskItem = ({ task, onEdit, showRecord = true, showAssignee = true }) => {
  const tenantId = useTenantKey()
  const queryClient = useQueryClient()
  const notify = useNotify()
  const [menu, setMenu] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const done = task.status === 'DONE'
  const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'tasks'] })
  const onError = (e) => notify(getErrorMessage(e), 'error')

  const toggle = useMutation({ mutationFn: () => (done ? tasksAPI.reopen(task.id) : tasksAPI.complete(task.id)), onSuccess: refresh, onError })
  const remove = useMutation({ mutationFn: () => tasksAPI.remove(task.id), onSuccess: () => { setConfirmDelete(false); refresh(); notify('Task deleted') }, onError })
  const reminder = REMINDERS.find((r) => r.value === task.remindMinutesBefore)

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, py: 0.75 }}>
      <Checkbox size="small" checked={done} disabled={!task.canEdit || toggle.isPending} onChange={() => toggle.mutate()} sx={{ mt: -0.5 }}
        inputProps={{ 'aria-label': done ? `Reopen ${task.title}` : `Complete ${task.title}` }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word', textDecoration: done ? 'line-through' : 'none', color: done ? 'text.secondary' : 'text.primary' }}>
          {task.title}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.25, alignItems: 'center' }}>
          <Tooltip title={task.dueAt ? formatDate(task.dueAt) : ''}>
            <Typography variant="caption" color={task.overdue ? 'error.main' : 'text.secondary'} sx={{ fontWeight: task.overdue ? 600 : 400 }}>
              {done ? `Done · ${dueLabel(task.completedOn)}` : dueLabel(task.dueAt)}{task.overdue ? ' · overdue' : ''}
            </Typography>
          </Tooltip>
          {!done && reminder && reminder.value !== '' && (
            <Tooltip title={`Reminder: ${reminder.label.toLowerCase()}`}>
              <NotificationsActiveOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} aria-label={`Reminder ${reminder.label.toLowerCase()}`} />
            </Tooltip>
          )}
          {showAssignee && <Typography variant="caption" color="text.secondary">{task.assignee.title}</Typography>}
          {showRecord && task.record && (
            task.record.deleted
              ? <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>{task.record.title}</Typography>
              : <Link component={RouterLink} to={`/records/${task.record.entityType}/${task.record.id}`} variant="caption" underline="hover">{task.record.title}</Link>
          )}
        </Box>
        {task.notes && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>{task.notes}</Typography>}
      </Box>
      {task.canEdit && (
        <>
          <IconButton size="small" onClick={(e) => setMenu(e.currentTarget)} aria-label={`Actions for ${task.title}`}><MoreVertIcon fontSize="small" /></IconButton>
          <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
            <MenuItem onClick={() => { setMenu(null); onEdit(task) }}>Edit</MenuItem>
            <MenuItem onClick={() => { setMenu(null); setConfirmDelete(true) }}>Delete</MenuItem>
          </Menu>
        </>
      )}
      <ConfirmDialog open={confirmDelete} title="Delete task?" message={`"${task.title}" will be removed.`} confirmLabel="Delete" destructive
        busy={remove.isPending} onConfirm={() => remove.mutate()} onClose={() => setConfirmDelete(false)} />
    </Box>
  )
}

export default TaskItem
